// safetyAuditForm/hooks/useSafetyAuditAutosave.js
// WebSocket-based autosave hook for Safety Audit form

import { useState, useEffect, useCallback, useRef } from "react";
import {
  initializeSafetyAuditSocket,
  joinSafetyAudit,
  leaveSafetyAudit,
  emitSafetyAuditPatch,
  onSafetyAuditConnectionChange,
} from "../services/safetyAuditSocketService";
import {
  queuePatch,
  flushQueue,
  getPendingCount,
} from "../services/safetyAuditPatchQueue";

export default function useSafetyAuditAutosave(
  safetyAuditId,
  autosaveErrorsActions = null,
) {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [saveStatus, setSaveStatus] = useState("idle"); // connected, saving, saved, queued, error
  const [pendingCount, setPendingCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const joinedRef = useRef(false);

  // Initialize socket and join room
  useEffect(() => {
    if (!safetyAuditId) return;

    initializeSafetyAuditSocket();

    const handleConnectionChange = async (connected) => {
      setConnectionStatus(connected ? "connected" : "disconnected");
      if (!connected) {
        // Reset joined flag on disconnect so we re-join on reconnect
        joinedRef.current = false;
      }
      if (connected && safetyAuditId) {
        try {
          if (!joinedRef.current) {
            await joinSafetyAudit(safetyAuditId);
            joinedRef.current = true;
          }
          const flushed = await flushQueue(safetyAuditId, emitSafetyAuditPatch);
          if (flushed > 0) {
            const remaining = await getPendingCount(safetyAuditId);
            setPendingCount(remaining);
          }
        } catch (err) {
          console.error("Error during Safety Audit socket reconnection:", err);
        }
      }
    };

    onSafetyAuditConnectionChange(handleConnectionChange);

    // Initial join
    joinSafetyAudit(safetyAuditId)
      .then(() => {
        joinedRef.current = true;
      })
      .catch((err) => console.error("Failed to join Safety Audit room:", err));

    // Update pending count
    getPendingCount(safetyAuditId)
      .then(setPendingCount)
      .catch((err) => console.error("Failed to get pending patch count:", err));

    return () => {
      if (safetyAuditId && joinedRef.current) {
        leaveSafetyAudit(safetyAuditId).catch((err) =>
          console.warn("Failed to leave Safety Audit room:", err),
        );
        joinedRef.current = false;
      }
    };
  }, [safetyAuditId]);

  // Emit patch with queue fallback
  // Returns: { success: boolean, error?: string, data?: object }
  const emitPatch = useCallback(
    async (patch) => {
      if (!safetyAuditId)
        return { success: false, error: "No Safety Audit ID" };

      setSaveStatus("saving");

      const result = await emitSafetyAuditPatch(safetyAuditId, patch);

      if (!result.error && !result.shouldQueue) {
        // Success - clear errors for this patch type
        setSaveStatus("saved");
        setLastError(null);
        setTimeout(() => setSaveStatus("idle"), 2000);

        // Clear field errors on successful save
        if (autosaveErrorsActions && patch.patch_type) {
          autosaveErrorsActions.clearPatchTypeErrors(patch.patch_type);
        }

        return { success: true, data: result.data };
      }

      if (result.shouldQueue) {
        // Network issue (disconnected, timeout, no response) - queue for retry
        try {
          await queuePatch(safetyAuditId, patch);
          const count = await getPendingCount(safetyAuditId);
          setPendingCount(count);
        } catch (idbErr) {
          console.warn(
            "Failed to queue Safety Audit patch to browser storage. Your changes may not be saved offline. Try refreshing the page if this persists.",
            idbErr?.message || idbErr,
          );
        }
        setSaveStatus("queued");
      } else {
        // Server validation error - show to user, DON'T queue
        setSaveStatus("error");
        setLastError(result.data || {});
        console.error("Safety Audit patch validation error:", result.error);

        // Use autosaveErrorsActions if available to set field-level errors
        if (autosaveErrorsActions && result.data?.error_type) {
          autosaveErrorsActions.handleServerError(result.data);
        }

        // If the error is unauthorized, redirect to login
        if (result.error === "Unauthorized") {
          window.location.href = "/login";
        }
        // Reset to idle after showing error briefly
        setTimeout(() => setSaveStatus("idle"), 3000);
      }

      return { success: false, error: result.error, data: result.data };
    },
    [safetyAuditId, autosaveErrorsActions],
  );

  return {
    emitPatch,
    connectionStatus,
    saveStatus,
    pendingCount,
    lastError,
  };
}
